'use client';

import { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AppKnowledgeSummary,
  KnowledgeSyncProviderType,
} from '@terra/shared-contracts-knowledge-sync';
import { toasterApi } from '@terra/ui-design-system';
import { knowledgeSourcesApi } from '../../clients/tester-client/knowledge-sources-api';
import { getProviderMappingIds } from '../../components/settings/target-settings/businessContext.transforms';

export const APP_KNOWLEDGE_SUMMARY_QUERY_KEY = 'app-knowledge-summary';

const EMPTY_SOURCES: AppKnowledgeSummary[] = [];

export function useAppKnowledgeSources({
  applicationId,
}: {
  applicationId: string | undefined;
}) {
  const t = useTranslations('settings.documents.knowledgeSources');
  const queryClient = useQueryClient();
  const summaryQuery = useQuery({
    queryKey: [APP_KNOWLEDGE_SUMMARY_QUERY_KEY, applicationId],
    queryFn: () => {
      if (!applicationId) {
        throw new Error('Application ID is required');
      }

      return knowledgeSourcesApi.getAppKnowledgeSummary(applicationId);
    },
    enabled: Boolean(applicationId),
    refetchOnWindowFocus: false,
  });
  const sources = summaryQuery.data?.sources ?? EMPTY_SOURCES;

  const removeProviderMutation = useMutation({
    mutationFn: async (mappingIds: string[]) => {
      await Promise.all(
        mappingIds.map((mappingId) =>
          knowledgeSourcesApi.deleteMapping(mappingId),
        ),
      );
    },
    onSuccess: async () => {
      toasterApi.success({
        title: t('mappingRemovedTitle'),
        description: t('mappingRemovedDescription'),
      });
      await queryClient.invalidateQueries({
        queryKey: [APP_KNOWLEDGE_SUMMARY_QUERY_KEY, applicationId],
      });
    },
    onError: (error) => {
      console.error(
        new Error('Failed to remove knowledge source mappings', {
          cause: error,
        }),
      );
      toasterApi.error({
        title: t('removeFailed'),
        description: error instanceof Error ? error.message : t('removeFailed'),
      });
    },
  });
  const { mutate: removeMappings } = removeProviderMutation;

  const removeProvider = useCallback(
    (provider: KnowledgeSyncProviderType) => {
      const mappingIds = getProviderMappingIds({ sources, provider });

      if (mappingIds.length > 0) {
        removeMappings(mappingIds);
      }
    },
    [removeMappings, sources],
  );

  return {
    sources,
    isLoading: summaryQuery.isLoading,
    error: summaryQuery.error,
    removeProvider,
    isRemoving: removeProviderMutation.isPending,
  };
}
